import { NotifyHelper } from 'classes/helpers/notify.helper';
import env from 'config';
import { ENV_PROD } from 'environments/i-prod';
import { ENV_PROD_JP } from 'environments/i-prod-jp';
import { ENV_PROD_KR } from 'environments/i-prod-kr';
import { first } from 'lodash';
import { useEffect } from 'react';
import useMiscStore from 'stores/use-misc-store';

const HOOK_NAME = 'useRegionRedirect';

const useRegionRedirect = () => {
  const readyToToast = useMiscStore((s) => s.readyToToast);

  useEffect(() => {
    if (!env.region_redirect) {
      return;
    }

    if (!readyToToast) {
      // wait until ready to toast
      return;
    }

    const timeout = setTimeout(
      () => {
        try {
          const showRedirectToast = (config: {
            url: string;
            country: string;
          }) => {
            NotifyHelper.warning({
              message_md: [
                `Are you visiting from ${config.country}?`,
                'Please use the following URL if you have issues connecting to your machine:',
                `[${config.url}](${config.url})`,
              ].join('\n\n'),
              delay_ms: 0,
              buttons: [
                {
                  label: 'common.dismiss',
                  onClick: () => {
                    // do nothing
                  },
                  dismissAfterClick: true,
                },
              ],
            });
          };

          // Extract two-letter ISO country code, e.g. ja-JP => JP, ko-KR => KR
          const languageCode = first(navigator.language.split('-'));

          // e.g. jp.ts.app
          const { hostname } = window.location;

          console.debug(
            `${HOOK_NAME}: found language ${languageCode} on ${hostname}`
          );

          switch (languageCode) {
            case 'ja': {
              if (hostname.startsWith('jp.')) {
                // already on jp site, do nothing
                console.debug(
                  `${HOOK_NAME}: already on jp site ${hostname} for language ${languageCode}`
                );
                return;
              }

              showRedirectToast({
                url: ENV_PROD_JP.self_url,
                country: 'Japan',
              });
              return;
            }

            case 'ko': {
              if (hostname.startsWith('kr.')) {
                // already on kr site, do nothing
                console.debug(
                  `${HOOK_NAME}: already on kr site ${hostname} for language ${languageCode}`
                );
                return;
              }

              showRedirectToast({
                url: ENV_PROD_KR.self_url,
                country: 'Korea',
              });
              return;
            }

            case 'en': {
              if (!hostname.startsWith('jp.') && !hostname.startsWith('kr.')) {
                // not on a non-english site, do nothing
                console.debug(
                  `${HOOK_NAME}: already on a suitable site ${hostname} for language ${languageCode}`
                );
                return;
              }

              showRedirectToast({
                url: ENV_PROD.self_url,
                country: 'North America',
              });
              return;
            }

            default: {
              console.debug(
                `${HOOK_NAME}: no redirect from ${hostname} configured for ${languageCode}`
              );
              return;
            }
          }
        } catch (e) {
          console.error(e);
        }
      },
      // ? sizeable delay to allow toast listener to mount and remount (react strict mode)
      3_000
    );

    return () => {
      clearTimeout(timeout);
    };
  }, [readyToToast]);
};

export default useRegionRedirect;
